import { CURRENT_SCHEMA_VERSION } from './schema';

/**
 * Version dispatch for shared URLs and persisted configs.
 *
 * Invariants:
 * - `upgraders` is indexed by the *source* version. Each entry upgrades from
 *   N -> N+1. We run them in order until we reach CURRENT_SCHEMA_VERSION.
 * - Upgraders may mutate the draft in place; the return value is ignored.
 * - NEVER delete an upgrader after shipping a new version. Old share links
 *   must keep working.
 *
 * When bumping CURRENT_SCHEMA_VERSION, add a new entry here and a
 * serialization round-trip snapshot test for the old -> new path.
 */
type Upgrader = (draft: Record<string, unknown>) => void;

const upgraders: Record<number, Upgrader> = {
  /**
   * v1 -> v2:
   *   - Adds `house.plinthHeight` (default 0) and `house.hasFrontSteps` (false).
   *   - Adds `stories[].partitionDoors` ([]) and `stories[].slabHoles` ([]).
   *   - Adds `stairs` ([]).
   *   - Adds `fence.style` ('rail' — matches v1 visual behaviour).
   *   - Adds `style.windowMullion` (false).
   * All defaults are picked so a v1 link renders identically pre-and-post upgrade,
   * minus the added details which are simply absent.
   */
  1: (draft) => {
    const house = (draft.house ?? {}) as Record<string, unknown>;
    if (typeof house.plinthHeight !== 'number') house.plinthHeight = 0;
    if (typeof house.hasFrontSteps !== 'boolean') house.hasFrontSteps = false;
    draft.house = house;

    const stories = Array.isArray(draft.stories) ? draft.stories : [];
    for (const s of stories) {
      const story = s as Record<string, unknown>;
      if (!Array.isArray(story.partitionDoors)) story.partitionDoors = [];
      if (!Array.isArray(story.slabHoles)) story.slabHoles = [];
    }
    draft.stories = stories;

    if (!Array.isArray(draft.stairs)) draft.stairs = [];

    if (draft.fence && typeof draft.fence === 'object') {
      const fence = draft.fence as Record<string, unknown>;
      if (typeof fence.style !== 'string') fence.style = 'rail';
    }

    const style = (draft.style ?? {}) as Record<string, unknown>;
    if (typeof style.windowMullion !== 'boolean') style.windowMullion = false;
    draft.style = style;
  },
  /**
   * v2 -> v3:
   *   - Adds `decks` ([]).
   *   - Extends each story's optional `balcony` with `hasRailing` (true)
   *     and `hasDoorToHouse` (true). Old configs without a balcony
   *     are unaffected.
   * Old configs render unchanged.
   */
  2: (draft) => {
    if (!Array.isArray(draft.decks)) draft.decks = [];
    const stories = Array.isArray(draft.stories) ? draft.stories : [];
    for (const s of stories) {
      const story = s as Record<string, unknown>;
      const bal = story.balcony as Record<string, unknown> | undefined;
      if (bal && typeof bal === 'object') {
        if (typeof bal.hasRailing !== 'boolean') bal.hasRailing = true;
        if (typeof bal.hasDoorToHouse !== 'boolean') bal.hasDoorToHouse = true;
      }
    }
  },
  /**
   * v3 -> v4:
   *   - Adds optional `basement` (below-grade story). Old configs are
   *     unaffected because the field is absent.
   *   - No other changes.
   */
  3: (_draft) => {
    // Nothing to populate — `basement` is optional.
  },
  /**
   * v4 -> v5:
   *   - `basement` gains explicit `footprintWidth/Depth/originX/originZ`.
   *     For old configs we backfill from the house body so the basement
   *     keeps matching the body footprint exactly.
   *   - `garage` gains `orientation` ('sideFacing' default = legacy)
   *     and `frontRecess` (0).
   *   - New optional `driveway` (omitted for old configs).
   */
  4: (draft) => {
    const house = (draft.house ?? {}) as Record<string, unknown>;
    const bodyW = typeof house.footprintWidth === 'number' ? house.footprintWidth : 0;
    const bodyD = typeof house.footprintDepth === 'number' ? house.footprintDepth : 0;
    const basement = draft.basement as Record<string, unknown> | undefined;
    if (basement && typeof basement === 'object') {
      if (typeof basement.footprintWidth !== 'number') basement.footprintWidth = bodyW;
      if (typeof basement.footprintDepth !== 'number') basement.footprintDepth = bodyD;
      if (typeof basement.originX !== 'number') basement.originX = 0;
      if (typeof basement.originZ !== 'number') basement.originZ = 0;
    }
    const garage = draft.garage as Record<string, unknown> | undefined;
    if (garage && typeof garage === 'object') {
      if (typeof garage.orientation !== 'string') garage.orientation = 'sideFacing';
      if (typeof garage.frontRecess !== 'number') garage.frontRecess = 0;
    }
  },
  /**
   * v5 -> v6:
   *   - `driveway` is now an axis-aligned bounding box (`xLo/xHi/zLo/zHi`)
   *     plus the `side` of the lot it crosses, instead of the legacy
   *     `xCenter/width/zFront/zBack` (which was implicitly always 'front').
   *     We rewrite old configs in place so old share links keep rendering.
   *   - Sideways garages now also get a driveway from the generator, but
   *     that's a generator-only change — old configs without a driveway
   *     stay without one.
   */
  /**
   * v7 -> v8:
   *   - Terraces moved to a corner-only encoding: `offset` is replaced
   *     by `corner: 'origin' | 'far'`. Any terrace that predates this
   *     migration used the mid-facade placement that we no longer
   *     render correctly, so we drop them on upgrade — the user can
   *     re-roll to get a new corner terrace.
   */
  7: (draft) => {
    if (Array.isArray(draft.terraces)) {
      draft.terraces = draft.terraces.filter((t: unknown) => {
        if (!t || typeof t !== 'object') return false;
        const rec = t as Record<string, unknown>;
        return typeof rec.corner === 'string';
      });
    }
  },
  /**
   * v8 -> v9:
   *   - `textures.ground` renamed to `textures.outdoorFloor` to match
   *     the on-disk `public/textures/outdoorFloor/` directory and to
   *     disambiguate from the lot's base plate proper (the underlying
   *     Three.js material). We migrate any existing selection so old
   *     share links keep rendering the same ground.
   */
  8: (draft) => {
    const tex = draft.textures as Record<string, unknown> | undefined;
    if (tex && typeof tex === 'object') {
      if (typeof tex.ground === 'string' && typeof tex.outdoorFloor !== 'string') {
        tex.outdoorFloor = tex.ground;
      }
      delete tex.ground;
    }
  },
  /**
   * v6 -> v7:
   *   - Adds top-level `terraces` ([]). Old configs are unaffected —
   *     they simply have no terraces.
   */
  6: (draft) => {
    if (!Array.isArray(draft.terraces)) draft.terraces = [];
  },
  5: (draft) => {
    const drv = draft.driveway as Record<string, unknown> | undefined;
    if (drv && typeof drv === 'object') {
      if (typeof drv.side !== 'string') drv.side = 'front';
      if (
        typeof drv.xLo !== 'number' &&
        typeof drv.xCenter === 'number' &&
        typeof drv.width === 'number'
      ) {
        const xc = drv.xCenter as number;
        const w = drv.width as number;
        drv.xLo = xc - w / 2;
        drv.xHi = xc + w / 2;
      }
      if (
        typeof drv.zLo !== 'number' &&
        typeof drv.zFront === 'number' &&
        typeof drv.zBack === 'number'
      ) {
        const zf = drv.zFront as number;
        const zb = drv.zBack as number;
        drv.zLo = Math.min(zf, zb);
        drv.zHi = Math.max(zf, zb);
      }
    }
  },
};

export interface UpgradeResult {
  readonly config: Record<string, unknown>;
  readonly fromVersion: number;
  readonly toVersion: number;
  readonly steps: number;
}

export function upgradeToLatest(input: unknown): UpgradeResult {
  if (input === null || typeof input !== 'object') {
    throw new Error('upgradeToLatest: input is not an object');
  }
  const draft = { ...(input as Record<string, unknown>) };
  const fromVersion = typeof draft.schemaVersion === 'number' ? draft.schemaVersion : 0;

  if (fromVersion > CURRENT_SCHEMA_VERSION) {
    throw new Error(
      `Config is from a newer schemaVersion (${fromVersion}) than this build supports (${CURRENT_SCHEMA_VERSION}).`,
    );
  }

  let steps = 0;
  let version = fromVersion;
  while (version < CURRENT_SCHEMA_VERSION) {
    const step = upgraders[version];
    if (!step) {
      throw new Error(`Missing upgrader for schemaVersion ${version}.`);
    }
    step(draft);
    version += 1;
    draft.schemaVersion = version;
    steps += 1;
  }

  return { config: draft, fromVersion, toVersion: version, steps };
}
